// Brightness-field functions for generative patterns. Each variant is a
// pure mathematical surface `(u, v, ctx) → brightness ∈ [0, 1]` evaluated
// per pixel.
//
// Used in two places:
//   1. `generateGenerativePng` — rasterises the field to a grayscale Blob
//      that gets uploaded to Pinata as the "shape" PNG for cover images.
//   2. `renderers/generative-grid.ts` — direct in-canvas render (used by
//      tests and any consumer that wants to draw the field without the
//      upload round-trip).
//
// The functions themselves don't know whether they're being rasterised or
// drawn — they just compute brightness. Same brightness function, same
// seed → same pixel values, every time.

import type { GenerativeVariant, LegacyGenerativeVariant } from '../types';
import { makeRng } from './rng';

const TWO_PI: number = Math.PI * 2;

export interface VariantContext {
  readonly seedInt: number;
  readonly phase1: number;
  readonly phase2: number;
  readonly phase3: number;
  readonly freq1: number;
  readonly freq2: number;
  readonly freq3: number;
  readonly tilt: number;
  readonly focusU: number;
  readonly focusV: number;
  readonly falloff: number;
}

export function buildVariantContext(seed: number): VariantContext {
  const rng = makeRng(seed);
  return {
    seedInt: seed | 0,
    phase1: rng() * TWO_PI,
    phase2: rng() * TWO_PI,
    phase3: rng() * TWO_PI,
    freq1: 1 + rng() * 3,
    freq2: 2 + rng() * 4,
    freq3: 4 + rng() * 6,
    tilt: rng() * TWO_PI,
    focusU: 0.3 + rng() * 0.4,
    focusV: 0.3 + rng() * 0.4,
    falloff: 1.2 + rng() * 1.5,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// Lattice-noise hash — uniform [0, 1] given integer (ix, iy) and a seed.
function hash2(ix: number, iy: number, seed: number): number {
  let h = (Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ seed) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = h ^ (h >>> 16);
  return (h >>> 0) / 4_294_967_296;
}

// ── Brightness functions — each returns [0, 1] ───────────────────────────

export function brightnessWaves(u: number, v: number, c: VariantContext): number {
  const tilted = u * Math.cos(c.tilt) + v * Math.sin(c.tilt);
  const dominant = Math.sin(tilted * TWO_PI * c.freq1 + c.phase1);
  const cross = Math.sin(u * TWO_PI * c.freq2 * 1.3 + c.phase2);
  const detail = Math.sin((u + v) * TWO_PI * c.freq3 * 1.6 + c.phase3);
  const combined = dominant * cross + detail * 0.35;
  const sign = combined < 0 ? -1 : 1;
  const expanded = sign * Math.min(1, Math.abs(combined) ** 0.55);
  return clamp((expanded + 1) / 2, 0, 1);
}

export function brightnessRings(u: number, v: number, c: VariantContext): number {
  const dx = u - c.focusU;
  const dy = v - c.focusV;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const wave = Math.sin(dist * TWO_PI * c.freq2 + c.phase1);
  return clamp((wave + 1) / 2, 0, 1);
}

export function brightnessStripes(u: number, v: number, c: VariantContext): number {
  const t = u * Math.cos(c.tilt) + v * Math.sin(c.tilt);
  const primary = Math.sin(t * TWO_PI * c.freq2 + c.phase1);
  const secondary = Math.sin(t * TWO_PI * c.freq3 + c.phase2) * 0.25;
  return clamp((primary + secondary + 1) / 2, 0, 1);
}

export function brightnessNoise(u: number, v: number, c: VariantContext): number {
  const scale = 4 + c.freq2;
  const x = u * scale;
  const y = v * scale;
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy, c.seedInt);
  const b = hash2(ix + 1, iy, c.seedInt);
  const cc = hash2(ix, iy + 1, c.seedInt);
  const d = hash2(ix + 1, iy + 1, c.seedInt);
  const top = a + (b - a) * sx;
  const bot = cc + (d - cc) * sx;
  return clamp(top + (bot - top) * sy, 0, 1);
}

export function brightnessBloom(u: number, v: number, c: VariantContext): number {
  const dx1 = u - c.focusU;
  const dy1 = v - c.focusV;
  const t1 = Math.max(0, 1 - Math.sqrt(dx1 * dx1 + dy1 * dy1) * c.falloff * 0.85);
  const g1 = t1 * t1 * (3 - 2 * t1);

  const dx2 = u - (1 - c.focusU);
  const dy2 = v - (1 - c.focusV);
  const t2 = Math.max(0, 1 - Math.sqrt(dx2 * dx2 + dy2 * dy2) * c.falloff * 1.2);
  const g2 = t2 * t2 * (3 - 2 * t2);

  const combined = g1 + g2 * 0.7;
  const wave = Math.sin((u + v) * TWO_PI * c.freq1 + c.phase1) * 0.1;
  return clamp(combined * 0.75 + wave + 0.18, 0, 1);
}

export type BrightnessFn = (u: number, v: number, c: VariantContext) => number;

export const VARIANT_FNS: Readonly<Record<LegacyGenerativeVariant, BrightnessFn>> = {
  waves: brightnessWaves,
  rings: brightnessRings,
  stripes: brightnessStripes,
  noise: brightnessNoise,
  bloom: brightnessBloom,
};

/**
 * Resolve a variant name to its brightness function. Falls back to
 * `waves` for unknown names so callers can persist variant strings
 * without crashing if a variant is later removed.
 */
export function getBrightnessFn(variant: GenerativeVariant): BrightnessFn {
  if ((variant as LegacyGenerativeVariant) in VARIANT_FNS) {
    return VARIANT_FNS[variant as LegacyGenerativeVariant];
  }
  return VARIANT_FNS.waves;
}
